import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, shareReplay } from 'rxjs';
import { environment } from '../../environments/environment';
import { apiBase } from '../cities/city-data';

export interface PublicStats {
    players: number;
    communities: number;
    /** Plays created on the platform. Synced BGG/BGA history is playsImported, not this. */
    playsLogged: number;
    playsImported: number;
    /** Standalone published events; the game tables organised inside them are tablesOrganised. */
    eventsHosted: number;
    tablesOrganised: number;
    /** Distinct users on at least one finished in-app play — signed in AND played. */
    playersWithLoggedPlays: number;
    /** Distinct players who opened the app in the last 30 days. */
    activeThisMonth: number;
    /** Distinct players who opened the app in the last 24 hours. */
    activeToday: number;
}

@Injectable({ providedIn: 'root' })
export class StatsService {
    /** One fetch per session — the homepage strip and every live stat block share it. */
    private stats$: Observable<PublicStats | null> | null = null;

    constructor(private http: HttpClient) {}

    getPublicStats(): Observable<PublicStats | null> {
        this.stats$ ??= this.http.get<PublicStats>(`${environment.apiUrl}/stats/public`).pipe(
            catchError(() => of(null)),
            shareReplay(1),
        );
        return this.stats$;
    }
}

/** Backoff between prerender attempts, so a rollout's few seconds of 503s cannot bake a blank strip. */
const RETRY_DELAYS_MS = [1_000, 3_000];

/**
 * The public stats for the prerender: plain fetch, retried, null when the API could not be reached
 * so the build never fails over them. The browser refreshes them through StatsService.
 */
export function fetchPublicStats(delays: readonly number[] = RETRY_DELAYS_MS): Promise<PublicStats | null> {
    return fetch(`${apiBase()}/stats/public`, {signal: AbortSignal.timeout(10_000)})
        .then(res => res.ok ? res.json() as Promise<PublicStats> : Promise.reject(new Error(`HTTP ${res.status}`)))
        .catch(() => delays.length === 0
            ? null
            : new Promise<PublicStats | null>(resolve =>
                setTimeout(() => resolve(fetchPublicStats(delays.slice(1))), delays[0])));
}
